import { useEffect, useState } from "react";
import { Save, Type, RotateCcw, PenLine } from "lucide-react";
import { toast } from "sonner";
import { CONTENT_SCHEMA, DEFAULT_CONTENT, HEADING_FONTS, BODY_FONTS } from "@/lib/content";
import { ImageUploadButton } from "@/components/EventCover";
import { resolveImageUrl } from "@/context/SettingsContext";

function CardImageField({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 flex-shrink-0 border border-zinc-700 rounded-sm bg-black/40 flex items-center justify-center overflow-hidden">
        {value ? (
          <img src={resolveImageUrl(value)} alt="" className="max-w-full max-h-full object-contain" data-testid={`${testid}-preview`} />
        ) : (
          <span className="text-[9px] tracking-[0.2em] uppercase text-zinc-600">Icon</span>
        )}
      </div>
      <ImageUploadButton testid={testid} label={value ? "Replace" : "Upload Image"} onUploaded={onChange} />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid={`${testid}-remove`}
          className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 hover:text-crimson-bright"
        >
          Remove
        </button>
      )}
    </div>
  );
}

function FontPicker({ label, value, options, onChange, testid, sample }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="tavern-input w-full px-3 py-2 rounded-sm"
        data-testid={testid}
      >
        {options.map((f) => <option key={f} value={f}>{f}</option>)}
      </select>
      <div className="mt-3 text-xl text-white" style={{ fontFamily: `'${value}'` }}>{sample}</div>
    </div>
  );
}

export function FontSection({ settings, updateSettings }) {
  const [heading, setHeading] = useState(settings.heading_font || "Cinzel");
  const [body, setBody] = useState(settings.body_font || "Outfit");
  const [saving, setSaving] = useState(false);

  const preload = (f) => {
    const id = `font-preview-${f.replace(/\s/g, "-")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${f.replace(/\s/g, "+")}:wght@400;700&display=swap`;
    document.head.appendChild(link);
  };
  useEffect(() => { [...HEADING_FONTS, ...BODY_FONTS].forEach(preload); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings({ heading_font: heading, body_font: body });
      toast.success("Fonts updated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save fonts");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="tavern-card p-6 sm:p-8" data-testid="admin-font-section">
      <div className="flex items-center gap-2 mb-6">
        <Type size={16} className="text-crimson-bright" />
        <h2 className="font-display tracking-[0.3em] uppercase text-sm text-white">Fonts</h2>
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <FontPicker label="Heading font" value={heading} options={HEADING_FONTS} onChange={setHeading} testid="admin-heading-font" sample="Ballhoot’s Tavern" />
        <FontPicker label="Body font" value={body} options={BODY_FONTS} onChange={setBody} testid="admin-body-font" sample="Pull up a stool, traveller." />
      </div>
      <button
        onClick={save}
        disabled={saving}
        data-testid="admin-save-fonts"
        className="btn-crimson mt-6 px-5 py-2 text-xs tracking-[0.3em] uppercase inline-flex items-center gap-2 disabled:opacity-60"
      >
        <Save size={14} /> {saving ? "Saving..." : "Save Fonts"}
      </button>
    </section>
  );
}

export function ContentSection({ settings, updateSettings }) {
  const [draft, setDraft] = useState(() => ({ ...DEFAULT_CONTENT, ...(settings.content || {}) }));
  const [group, setGroup] = useState(CONTENT_SCHEMA[0].group);
  const [saving, setSaving] = useState(false);
  const current = CONTENT_SCHEMA.find((g) => g.group === group);

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings({ content: draft });
      toast.success("Text updated across the tavern.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save text");
    } finally {
      setSaving(false);
    }
  };

  const saveImage = async (key, url) => {
    const next = { ...draft, [key]: url };
    setDraft(next);
    try {
      await updateSettings({ content: next });
      toast.success(url ? "Card image saved." : "Card image removed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save image");
    }
  };

  const resetGroup = () => {
    const next = { ...draft };
    current.fields.forEach(([key]) => { next[key] = DEFAULT_CONTENT[key] ?? ""; });
    setDraft(next);
    toast.message(`${group} text reset. Click Save to apply.`);
  };

  return (
    <section className="tavern-card p-6 sm:p-8" data-testid="admin-content-section">
      <div className="flex items-center gap-2 mb-6">
        <PenLine size={16} className="text-crimson-bright" />
        <h2 className="font-display tracking-[0.3em] uppercase text-sm text-white">Site Text</h2>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {CONTENT_SCHEMA.map((g) => (
          <button
            key={g.group}
            onClick={() => setGroup(g.group)}
            data-testid={`admin-text-tab-${g.group.toLowerCase()}`}
            className={`${group === g.group ? "btn-crimson" : "btn-ghost"} px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase`}
          >
            {g.group}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {current.fields.map(([key, label, multiline]) => (
          <div key={key} className={multiline ? "sm:col-span-2" : ""}>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">{label}</label>
            {multiline === "image" ? (
              <CardImageField
                value={draft[key]}
                testid={`admin-text-${key.replace(".", "-")}`}
                onChange={(url) => saveImage(key, url)}
              />
            ) : multiline ? (
              <textarea
                rows={key === "about.extra" ? 5 : 2}
                maxLength={4000}
                className="tavern-input w-full px-3 py-2 rounded-sm"
                value={draft[key] ?? ""}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                data-testid={`admin-text-${key.replace(".", "-")}`}
              />
            ) : (
              <input
                maxLength={1000}
                className="tavern-input w-full px-3 py-2 rounded-sm"
                value={draft[key] ?? ""}
                onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                data-testid={`admin-text-${key.replace(".", "-")}`}
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 mt-6">
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-save-text"
          className="btn-crimson px-5 py-2 text-xs tracking-[0.3em] uppercase inline-flex items-center gap-2 disabled:opacity-60"
        >
          <Save size={14} /> {saving ? "Saving..." : "Save All Text"}
        </button>
        <button
          onClick={resetGroup}
          data-testid="admin-reset-text"
          className="btn-ghost px-4 py-2 text-xs tracking-[0.3em] uppercase inline-flex items-center gap-2"
        >
          <RotateCcw size={14} /> Reset {group}
        </button>
      </div>
    </section>
  );
}
